"use client";

import type React from "react";
import { Stethoscope, Calendar, Clock, PhoneCall, Ambulance } from "lucide-react";
import Image from "next/image";
import TeamsSection from "@/components/teams-section";
import RoomsSection from "@/components/rooms-section";
import HealthcareProducts from "@/components/products";
import AboutSection from "@/components/welcome-section";
import Link from "next/link";
import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import ServiceCard from "@/components/service-card";
import TreatementSection from "@/components/treatment-section";
import { useGoogleAdsConversion } from "@/hooks/useGoogleAdsConversion";
import { testGoogleAdsSetup } from "@/utils/testConversion";

export default function Home() {
  const { trackConversion } = useGoogleAdsConversion();
  
  const phone = "+628123954567";
  const message = encodeURIComponent(
    `Hello Dr. Ari, I would like to Book your Appointment.`
  );
  const whatsappLink = `https://wa.me/${phone}?text=${message}`;
  
  const heroRef = useRef(null);
  const isHeroInView = useInView(heroRef, { once: true });
  const imageRef = useRef(null);
  const isImageInView = useInView(imageRef, { once: true });

  // Handler untuk conversion tracking
  const handleBookAppointmentClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    
    // Debug in development
    if (process.env.NODE_ENV === 'development') {
      // console.log('🎯 Conversion button clicked');
      // Delay testing to ensure scripts are loaded
      setTimeout(() => {
        if (typeof window !== 'undefined') {
          testGoogleAdsSetup();
        }
      }, 500);
    }
    
    trackConversion(whatsappLink);
  };

  const services = [
    {
      icon: <Calendar className='text-teal-500' />,
      title: "Appointment",
      description: "Book an appointment to schedule a consultation with a doctor.",
    },
    {
      icon: <Clock className='text-teal-500' />,
      title: "24 Working Hours",
      description: "Available 24/7 to help with your health issue.",
    },
    {
      icon: <PhoneCall className='text-teal-500' />,
      title: "On Call Service",
      description: "Medical care anytime, anywhere—with our 24/7 on-call doctor service.",
    },
    {
      icon: <Ambulance className='text-teal-500' />,
      title: "Ambulance",
      description: "Fast and reliable ambulance service, available 24/7 for emergency medical assistance.",
    },
  ];

  return (
    <main>
      <div className='bg-gradient-to-r lg:h-[820px] from-[#4DAF9F] to-[#58DEC8]'>
        <div className='lg:relative lg:flex lg:justify-center w-full p-4 lg:p-0 lg:py-[46px]'>
          <motion.div ref={imageRef} initial={{ opacity: 0, y: 100 }} animate={isImageInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.8, ease: "easeOut" }}>
            <Image
              src='/images/hero-banner.webp'
              alt='Doctor'
              width={1225}
              height={1000}
              className='object-contain'
              quality={75}
              priority
              sizes='(max-width: 768px) 100vw, 50vw'
            />
          </motion.div>

          <div className='lg:absolute lg:w-[1100px] flex lg:min-h-[603px] text-white'>
            <motion.div ref={heroRef} initial={{ opacity: 0, y: 100 }} animate={isHeroInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.8, ease: "easeOut" }} className='py-12 lg:py-0 flex items-center lg:h-[565px]'>
              <div className='lg:max-w-full'>
                <div className='logo-icons flex gap-3 items-center mb-4'>
                  <Stethoscope color='white' size={20} />
                  <h1 className='text-sm lg:text-xl lg:font-semibold md:text-base'>WELCOME TO DR. ARI SUDHEWA & ASSOCIATES</h1>
                </div>

                <div className='max-w-[554px] mb-6'>
                  <h1 className='text-3xl md:text-5xl md:font-bold font-semibold leading-tight'>Dr Ari, the most Trusted Doctor for your health.</h1>
                </div>

                <div className='max-w-[553px] mb-8 lg:mb-0'>
                  <p className='font-normal lg:text-3xl'>Be careful! Dr. Ari can only be contacted through these two WhatsApp numbers:</p>
                  <p className='font-semibold outline-text lg:text-2xl w-[300px] mt-3 lg:mt-8 lg:mb-12 mb-8'>
                    WA 1 : +62 812-3954-567 <br />
                    WA 2 : +62 812-3924-814
                  </p>
                  <Link 
                    href={whatsappLink} 
                    target='_blank' 
                    rel='noopener noreferrer'
                    onClick={handleBookAppointmentClick}
                    className='bg-amber-500 py-[10px] mt-8 px-[24px] hover:bg-amber-300 hover:cursor-pointer rounded-md font-medium'
                  >
                    Book Your Appointment
                  </Link>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Services Section */}
      <div className='bg-white shadow-md rounded-[32px] mx-4 lg:mx-[145px] lg:-mt-30 -mt-10 lg:relative z-10'>
        <div className='grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6 lg:py-5 justify-items-center'>
          {services.map((service, index) => (
            <ServiceCard key={index} icon={service.icon} title={service.title} description={service.description} />
          ))}
        </div>
      </div>
      
      <div className='py-12 lg:py-32 px-4 sm:px-6 lg:px-[145px]'>
        <div className='flex flex-col lg:flex-row justify-between items-center gap-6'>
          <div className='lg:basis-1/2'>
            <h2 className='text-2xl sm:text-3xl font-bold text-teal-500 mb-4'>Our Clinic</h2>
            <p>A glimpse of our trusted facility, serving patients with dedication and professionalism.</p>
          </div>
          <div>
            <Image src='/images/building.webp' alt='Logo' width={400} height={600} className='rounded-[25px] object-cover lg:px-0' />
          </div>
        </div>
      </div>

      <AboutSection />
      <TeamsSection />
      <RoomsSection />
      <HealthcareProducts />
      <TreatementSection />
    </main>
  );
}
